/* Vove: PDP add-on recommendations. Added 14/08/2026.

   Fills the card rendered by snippets/vove-pdp-addon.liquid from Shopify's own
   product recommendations, so the offer follows what shoppers actually buy
   together instead of four products pinned once in the theme editor.

   Why client side. Recommendations are per product and change as the store
   learns, so they cannot be baked into a cached section render, and the card
   is custom markup rather than a product card, which rules out the
   section-rendering form of the endpoint. So the endpoint is asked for JSON
   and this script writes the four fields that differ (image, title, link,
   variant id and price) into markup Liquid already put on the page. Nothing is
   built from strings here: the skeleton, the theme's <product-form>, its error
   region and its loading indicator all come from Liquid, which is what keeps
   the add on the theme's own AJAX path.

   Progressive enhancement. The card is either server-rendered with a pinned
   fallback candidate and visible, or rendered hidden with no content. This
   script only ever fills fields and un-hides. If the request fails, returns
   nothing, or this file never runs, the page keeps whichever of those two
   states Liquid chose, so an empty bordered box cannot appear.

   Quick-add and drawer contexts never reach this file: the card carries the
   theme's not-quick-add attribute, so assets/quick-add.js strips it out of the
   markup it fetches before that markup is ever inserted. */
(function () {
  "use strict";

  var INTENTS = ["complementary", "related"];
  var LIMIT = 10;

  /* Reduce prose to single-spaced lowercase words, padded with one space at
     each end so a single word can be probed with indexOf. Same normalising
     the Liquid side does to the "what's included" text, so both sides of a
     comparison are shaped the same way. */
  function normalise(text) {
    return (
      " " +
      String(text || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim() +
      " "
    );
  }

  /* Carried over from the Liquid rule this replaced: a phrase counts as
     present when every one of its words is in the prose, in any order and not
     necessarily adjacent, with a trailing "s" stripped from both sides so
     "loofah sponges" matches "4 loofah scrub sponges". Substring matching was
     tried first and failed silently on one inserted word. */
  function phraseInHaystack(phrase, haystack) {
    var words = normalise(phrase).trim().split(" ");
    var checked = 0;
    for (var i = 0; i < words.length; i++) {
      var word = words[i];
      if (!word) continue;
      checked += 1;
      var stem = word.length > 1 && word.slice(-1) === "s" ? word.slice(0, -1) : word;
      if (haystack.indexOf(" " + stem + " ") === -1 && haystack.indexOf(" " + stem + "s ") === -1) {
        return false;
      }
    }
    return checked > 0;
  }

  function alreadyIncluded(product, variant, haystack) {
    if (haystack.trim() === "") return false;
    var phrases = [product.title, product.handle, variant.sku];
    for (var i = 0; i < phrases.length; i++) {
      if (phrases[i] && phraseInHaystack(phrases[i], haystack)) return true;
    }
    return false;
  }

  /* First recommendation that is not the product being viewed, has a variant
     that can actually be bought, and is not part of what this product already
     contains. Bundles are deliberately not excluded: a bundle page is where an
     extra earns the most. */
  function pick(products, currentId, haystack) {
    for (var i = 0; i < products.length; i++) {
      var product = products[i];
      if (!product || String(product.id) === currentId) continue;
      var variants = product.variants || [];
      var variant = null;
      var variantIndex = -1;
      for (var v = 0; v < variants.length; v++) {
        if (variants[v] && variants[v].available) {
          variant = variants[v];
          variantIndex = v;
          break;
        }
      }
      if (!variant) continue;
      /* Same rule the Liquid side applies: a variant past the first that has
         no photo of its own would be drawn with the parent's shot, so the
         card would show one thing and add another. Skip the candidate rather
         than show a picture that is not what the button buys. The first
         variant is exempt because the parent shot is its shot. */
      if (variantIndex > 0 && !(variant.featured_image && variant.featured_image.src)) continue;
      if (alreadyIncluded(product, variant, haystack)) continue;
      return { product: product, variant: variant };
    }
    return null;
  }

  function money(minorUnits, currency, locale) {
    var amount = Number(minorUnits) / 100;
    try {
      return new Intl.NumberFormat(locale || undefined, {
        style: "currency",
        currency: currency,
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + " " + currency;
    }
  }

  /* The recommendations JSON hands back full-size CDN URLs. The CDN takes a
     width parameter, which is how the same srcset ladder the server render
     uses is rebuilt here. */
  function sized(url, width) {
    if (!url) return "";
    return url + (url.indexOf("?") === -1 ? "?" : "&") + "width=" + width;
  }

  /* Prefer the variant's own photo, because the variant is what gets added.
     Intrinsic dimensions come with it, and with the first media entry when it
     does not, so the thumbnail reserves its box before the file arrives. The
     product-level featured_image is a bare URL with no dimensions, which is
     why it is the last resort. */
  function imageFor(product, variant) {
    if (variant.featured_image && variant.featured_image.src) {
      return {
        src: variant.featured_image.src,
        width: variant.featured_image.width,
        height: variant.featured_image.height,
      };
    }
    var media = product.media || [];
    for (var i = 0; i < media.length; i++) {
      if (media[i] && media[i].media_type === "image" && media[i].src) {
        return { src: media[i].src, width: media[i].width, height: media[i].height };
      }
    }
    if (product.featured_image) return { src: product.featured_image, width: 0, height: 0 };
    return null;
  }

  function fillCard(root, winner, config) {
    var product = winner.product;
    var variant = winner.variant;

    var title = product.title;
    if ((product.variants || []).length > 1 && variant.title && variant.title !== "Default Title") {
      title = product.title + ", " + variant.title;
    }

    var url = variant.url || product.url;
    var image = imageFor(product, variant);

    var media = root.querySelector("[data-addon-media]");
    var img = root.querySelector("[data-addon-image]");
    if (media && img) {
      if (image) {
        img.setAttribute("src", sized(image.src, 160));
        img.setAttribute(
          "srcset",
          sized(image.src, 80) +
            " 80w, " +
            sized(image.src, 160) +
            " 160w, " +
            sized(image.src, 240) +
            " 240w"
        );
        if (image.width && image.height) {
          img.setAttribute("width", image.width);
          img.setAttribute("height", image.height);
        } else {
          img.removeAttribute("width");
          img.removeAttribute("height");
        }
        media.setAttribute("href", url);
        media.hidden = false;
      } else {
        media.hidden = true;
      }
    }

    var link = root.querySelector("[data-addon-title]");
    if (link) {
      link.textContent = title;
      link.setAttribute("href", url);
    }

    var input = root.querySelector("[data-addon-variant-id]");
    if (input) input.value = variant.id;

    var price = root.querySelector("[data-addon-price]");
    if (price) price.textContent = money(variant.price, config.currency, config.locale);

    var button = root.querySelector("[data-addon-button]");
    if (button) button.setAttribute("aria-label", config.buttonLabel + " " + title);
  }

  function request(url) {
    return fetch(url, { headers: { Accept: "application/json" } }).then(function (response) {
      if (!response.ok) throw new Error("Recommendations request failed");
      return response.json();
    });
  }

  /* Ask for complementary first, because a product that pairs with this one is
     a better add than one that merely resembles it. Fall back to related when
     complementary has nothing usable, which is the common case on a product
     Search and Discovery has not learned yet. */
  function load(root, config, index) {
    if (index >= INTENTS.length) return;

    var url =
      config.endpoint +
      (config.endpoint.indexOf("?") === -1 ? "?" : "&") +
      "product_id=" +
      encodeURIComponent(config.productId) +
      "&limit=" +
      LIMIT +
      "&intent=" +
      INTENTS[index];

    request(url)
      .then(function (data) {
        var winner = pick((data && data.products) || [], config.productId, config.haystack);
        if (winner) {
          fillCard(root, winner, config);
          root.hidden = false;
          return;
        }
        load(root, config, index + 1);
      })
      .catch(function () {
        /* Leave whatever Liquid rendered: the pinned fallback if there was
           one, otherwise the hidden empty skeleton. */
      });
  }

  function init() {
    var cards = document.querySelectorAll("[data-addon-endpoint]");
    for (var i = 0; i < cards.length; i++) {
      var root = cards[i];
      if (root.dataset.addonReady === "true") continue;
      root.dataset.addonReady = "true";

      var endpoint = root.dataset.addonEndpoint;
      var productId = root.dataset.addonProductId;
      if (!endpoint || !productId) continue;

      load(
        root,
        {
          endpoint: endpoint,
          productId: String(productId),
          currency: root.dataset.addonCurrency || "USD",
          locale: root.dataset.addonLocale || "",
          buttonLabel: root.dataset.addonButtonLabel || "Add",
          haystack: normalise(root.dataset.addonIncluded || ""),
        },
        0
      );
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
